import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import openNotification from '../../components/Common/OpenNotification';
import { ADMIN_GET_UNIT_REGION_OTHER_PROJECTS, ADMIN_GET_UNIT_REGION_OTHER_PROJECTS_DETAILS, CALCULATE_OTHER_PROJECTS, GET_CUSTOM_CARD_OTHER_PROJECTS, GET_CUSTOM_CARD_OTHER_PROJECTS_VERSIONED, GET_OTHER_PROJECT_PROFIT_LOSS, GET_OTHER_PROJECT_PROFIT_LOSS_VERSIONED, GET_OTHER_PROJECT_REVENUE, GET_OTHER_PROJECT_REVENUE_VERSIONED, GET_OTHER_PROJECT_SUMMARY, GET_OTHER_PROJECT_SUMMARY_VERSIONED, GET_OTHER_PROJECTS, GET_OTHER_PROJECTS_TABLES_DATA, GET_OTHER_PROJECTS_DATE, SET_OTHER_PROJECTS_DATE } from '../../api/endpoints';
import { RemoveEmptyKey } from '../../utils/removeEmptyKey';
import { version } from 'react';

const initialState = {
        loading: false,
        otherProjects: [],
        tablesData: [],
        summaryData: {},
        cardData: [],
        revenueData: [],
        profitLossData: [],
        profitLossDataVersioned: [],
        otherProjectListing: [],
        otherProjectDetailsListing: [],
        otherProjectTotals: {},
        otherProjectsDate: {},
        settingOtherProjectsDate: false,
        systemDate: null,
        systemDateLoading: false,
};
export const getOtherProjectsDate = createAsyncThunk(
        "otherProjects/getDate",
        async (type = "other", { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                `${GET_OTHER_PROJECTS_DATE}?type=${type}`
                        );

                        return response.data;
                } catch (error) {
                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);

// Cached system date (STARTDATE only is used), fetched once app-wide and
// reused by any component instead of each one calling the API separately.
export const getSystemDate = createAsyncThunk(
        "otherProjects/getSystemDate",
        async (_, { rejectWithValue }) => {
                try {
                        const response = await axiosInstance.get(
                                `${GET_OTHER_PROJECTS_DATE}?type=system`
                        );

                        return response.data;
                } catch (error) {
                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);

export const setOtherProjectsDate = createAsyncThunk(
        "otherProjects/setDate",
        async (payload, { rejectWithValue }) => {
                try {
                        const { configName, ...body } = payload;
                        const response = await axiosInstance.post(
                                SET_OTHER_PROJECTS_DATE,
                                payload
                        );

                        // openNotification(
                        //         "success",
                        //         response?.data?.message || "Date updated successfully"
                        // );

                        openNotification(
                                "success",
                                `${configName} Date updated successfully`
                        );

                        return response.data;
                } catch (error) {
                        openNotification(
                                "error",
                                error?.response?.data?.message || "Something went wrong."
                        );

                        return rejectWithValue(
                                error?.response?.data?.message || "Something went wrong."
                        );
                }
        }
);

export const getOtherProjects = createAsyncThunk('otherProjects/getOtherProjects', async ({ page, pageSize, search, mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_OTHER_PROJECTS, {
                        params,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const calculateOtherProjects = createAsyncThunk('otherProjects/calculateOtherProjects', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(CALCULATE_OTHER_PROJECTS);
                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getOtherProjectsTablesData = createAsyncThunk('otherProjects/getOtherProjectsTablesData', async ({ page, pageSize, tableName, search, mnfUnit, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (page) params.page = page;
                if (pageSize) params.limit = pageSize;
                if (search) params.search = search;
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (version !== "C") params.version = version;

                const url = GET_OTHER_PROJECTS_TABLES_DATA[tableName];
                const response = await axiosInstance.get(url, {
                        params,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getOtherProjectsRevenueData = createAsyncThunk('otherProjects/getOtherProjectsRevenueData', async ({ mnfUnit, search }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (search) params.search = search;

                const url = GET_OTHER_PROJECT_REVENUE;
                const response = await axiosInstance.get(url, { params });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getOtherProjectsRevenueDataVersioned = createAsyncThunk('otherProjects/getOtherProjectsRevenueDataVersioned', async ({ mnfUnit, search, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (search) params.search = search;
                if (version) params.version = version;

                const url = GET_OTHER_PROJECT_REVENUE_VERSIONED;
                const response = await axiosInstance.get(url, { params });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getOtherProjectsProfitLossData = createAsyncThunk('otherProjects/getOtherProjectsProfitLossData', async ({ mnfUnit, search }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (search) params.search = search;

                const url = GET_OTHER_PROJECT_PROFIT_LOSS;
                const response = await axiosInstance.get(url, { params });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getOtherProjectsProfitLossDataVersioned = createAsyncThunk('otherProjects/getOtherProjectsProfitLossDataVersioned', async ({ mnfUnit, search, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (search) params.search = search;
                if (version) params.version = version;
                const url = GET_OTHER_PROJECT_PROFIT_LOSS_VERSIONED;
                const response = await axiosInstance.get(url, { params });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getOtherProjectSummary = createAsyncThunk('otherProjects/getOtherProjectSummary', async ({ mnfUnit }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;

                const response = await axiosInstance.get(GET_OTHER_PROJECT_SUMMARY, {
                        params,
                });
                return response?.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getOtherProjectSummaryVersioned = createAsyncThunk('otherProjects/getOtherProjectSummaryVersioned', async ({ mnfUnit, version }, { rejectWithValue }) => {
        try {
                const params = {};
                if (mnfUnit) params.MNF_UNIT = mnfUnit;
                if (version) params.version = version;
                const response = await axiosInstance.get(GET_OTHER_PROJECT_SUMMARY_VERSIONED, {
                        params,
                });
                return response?.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getotherProjectsCustomCardDetail = createAsyncThunk('region/getCustomCardDetail', async (params, { rejectWithValue }) => {
        try {
                const queryParams = {};
                if (params?.monthRange) {
                        const { from_date, to_date } = params.monthRange;
                        if (from_date) queryParams.from_date = from_date;
                        if (to_date) queryParams.to_date = to_date;
                }

                if (params.mnfUnit) queryParams.MNF_UNIT = params.mnfUnit;
                const response = await axiosInstance.get(GET_CUSTOM_CARD_OTHER_PROJECTS, {
                        params: queryParams,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});
export const getotherProjectsVersionedCustomCard = createAsyncThunk('region/getCustomCardDetailVersioned', async (params, { rejectWithValue }) => {
        try {
                const queryParams = {};
                if (params?.monthRange) {
                        const { from_date, to_date } = params.monthRange;
                        if (from_date) queryParams.from_date = from_date;
                        if (to_date) queryParams.to_date = to_date;
                }

                if (params.mnfUnit) queryParams.MNF_UNIT = params.mnfUnit;
                if (params.version) queryParams.version = params.version;
                const response = await axiosInstance.get(GET_CUSTOM_CARD_OTHER_PROJECTS_VERSIONED, {
                        params: queryParams,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getRegionUnitOtherProjectsListing = createAsyncThunk('otherProjects/getRegionUnitOtherProjectsListing', async (params, { rejectWithValue }) => {
        const payload = RemoveEmptyKey({
                page: params?.page,
                limit: params?.limit,
                MNF_UNIT: params.mnfUnits,
                search: params?.search,
        });
        if (params?.monthRange) {
                const { from_date, to_date } = params.monthRange;
                if (from_date) {
                        payload.from_month = from_date.split('-')[1];
                        payload.from_year = from_date.split('-')[2];
                }

                if (to_date) {
                        payload.to_month = to_date.split('-')[1];
                        payload.to_year = to_date.split('-')[2];

                }
        }

        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_OTHER_PROJECTS, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getRegionUnitOtherProjectsDetailsListing = createAsyncThunk('otherProjects/getRegionUnitOtherProjectsDetailsListing', async (params, { rejectWithValue }) => {
        const payload = RemoveEmptyKey({
                page: params?.page,
                limit: params?.limit,
                MNF_UNIT: params.mnfUnits,
                IO_NO: params.IO_NO,
                REV_NO: params.REV_NO,
        });

        if (params?.monthRange) {
                const { from_date, to_date } = params.monthRange;
                if (from_date) {
                        payload.from_month = from_date.split('-')[1];
                        payload.from_year = from_date.split('-')[2];
                }

                if (to_date) {
                        payload.to_month = to_date.split('-')[1];
                        payload.to_year = to_date.split('-')[2];
                }
        }

        try {
                const response = await axiosInstance.get(ADMIN_GET_UNIT_REGION_OTHER_PROJECTS_DETAILS, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

const otherProjectsSlice = createSlice({
        name: 'otherProjects',
        initialState,
        reducers: {},
        extraReducers: (builder) => {
                builder
                        // Reducer for fetch operating performance
                        .addCase(getOtherProjects.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjects.fulfilled, (state, action) => {
                                state.loading = false;
                                state.otherProjects = action.payload.data?.data;
                                state.totalPagesOtherProjects = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getOtherProjects.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        // Reducer for fetch operating performance
                        .addCase(getOtherProjectsTablesData.pending, (state) => {
                                state.tableLoading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectsTablesData.fulfilled, (state, action) => {
                                state.tableLoading = false;
                                state.tablesData = action.payload.data?.data;
                                state.totalPagesOtherProjects = action?.payload?.data?.pagination?.total;
                        })
                        .addCase(getOtherProjectsTablesData.rejected, (state, action) => {
                                state.tableLoading = false;
                                state.error = action.payload;
                        })
                        // Reducer for fetch operating performance
                        .addCase(getOtherProjectsRevenueData.pending, (state) => {
                                state.tableLoading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectsRevenueData.fulfilled, (state, action) => {
                                state.tableLoading = false;
                                state.revenueData = action.payload.data;
                        })
                        .addCase(getOtherProjectsRevenueData.rejected, (state, action) => {
                                state.tableLoading = false;
                                state.error = action.payload;
                        })
                        .addCase(getOtherProjectsRevenueDataVersioned.pending, (state) => {
                                state.tableLoading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectsRevenueDataVersioned.fulfilled, (state, action) => {
                                state.tableLoading = false;
                                state.revenueDataVersioned = action.payload.data;
                        })
                        .addCase(getOtherProjectsRevenueDataVersioned.rejected, (state, action) => {
                                state.tableLoading = false;
                                state.error = action.payload;
                        })
                        // Reducer for fetch operating performance
                        .addCase(getOtherProjectsProfitLossData.pending, (state) => {
                                state.tableLoading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectsProfitLossData.fulfilled, (state, action) => {
                                state.tableLoading = false;
                                state.profitLossData = action.payload.data;
                        })
                        .addCase(getOtherProjectsProfitLossData.rejected, (state, action) => {
                                state.tableLoading = false;
                                state.error = action.payload;
                        })
                        .addCase(getOtherProjectsProfitLossDataVersioned.pending, (state) => {
                                state.tableLoading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectsProfitLossDataVersioned.fulfilled, (state, action) => {
                                state.tableLoading = false;
                                state.profitLossDataVersioned = action.payload.data;
                        })
                        .addCase(getOtherProjectsProfitLossDataVersioned.rejected, (state, action) => {
                                state.tableLoading = false;
                                state.error = action.payload;
                        })

                        // ✅ Add this block for getOtherProjectSummary
                        .addCase(getOtherProjectSummary.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectSummary.fulfilled, (state, action) => {
                                state.loading = false;
                                state.summaryData = action.payload?.data; // Update state as needed
                        })
                        .addCase(getOtherProjectSummary.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getOtherProjectSummaryVersioned.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getOtherProjectSummaryVersioned.fulfilled, (state, action) => {
                                state.loading = false;
                                state.summaryDataVersioned = action.payload?.data; // Update state as needed
                        })
                        .addCase(getOtherProjectSummaryVersioned.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getotherProjectsCustomCardDetail.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getotherProjectsCustomCardDetail.fulfilled, (state, action) => {
                                state.loading = false;
                                state.cardData = action.payload?.data; // Update state as needed
                        })
                        .addCase(getotherProjectsCustomCardDetail.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getotherProjectsVersionedCustomCard.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getotherProjectsVersionedCustomCard.fulfilled, (state, action) => {
                                state.loading = false;
                                state.cardVersionedData = action.payload?.data; // Update state as needed
                        })
                        .addCase(getotherProjectsVersionedCustomCard.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getRegionUnitOtherProjectsListing.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getRegionUnitOtherProjectsListing.fulfilled, (state, action) => {
                                state.loading = false;
                                state.otherProjectListing = action.payload?.data?.data; // Update state as needed
                                state.otherProjectListingTotal = action.payload?.data?.total; // Update state as needed
                                state.otherProjectTotals = action.payload?.data?.totals; 
                        })
                        .addCase(getRegionUnitOtherProjectsListing.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getRegionUnitOtherProjectsDetailsListing.pending, (state) => {
                                state.loading = true;
                                state.error = null;
                        })
                        .addCase(getRegionUnitOtherProjectsDetailsListing.fulfilled, (state, action) => {
                                state.loading = false;
                                state.otherProjectDetailsListing = action.payload?.data?.data; // Update state as needed
                                state.otherProjectDetailsListingTotal = action.payload?.data?.total; // Update state as needed
                        })
                        .addCase(getRegionUnitOtherProjectsDetailsListing.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getOtherProjectsDate.pending, (state) => {
                                state.loading = true;
                        })
                        .addCase(getOtherProjectsDate.fulfilled, (state, action) => {
                                state.loading = false;
                                state.otherProjectsDate = action.payload.data;

                                if (action.meta.arg === "system") {
                                        state.systemDate = action.payload.data;
                                }
                        })
                        .addCase(getOtherProjectsDate.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(setOtherProjectsDate.pending, (state) => {
                                state.settingOtherProjectsDate = true;
                        })
                        .addCase(setOtherProjectsDate.fulfilled, (state) => {
                                state.settingOtherProjectsDate = false;
                        })
                        .addCase(setOtherProjectsDate.rejected, (state, action) => {
                                state.settingOtherProjectsDate = false;
                                state.error = action.payload;
                        })
                        .addCase(getSystemDate.pending, (state) => {
                                state.systemDateLoading = true;
                        })
                        .addCase(getSystemDate.fulfilled, (state, action) => {
                                state.systemDateLoading = false;
                                state.systemDate = action.payload.data;
                        })
                        .addCase(getSystemDate.rejected, (state, action) => {
                                state.systemDateLoading = false;
                                state.error = action.payload;
                        });
        },
});

export default otherProjectsSlice.reducer;
